/* global FontName, FontTypekitId */
/* eslint-disable import/prefer-default-export */

import React from 'react';
import { Helmet } from 'react-helmet';
import _ from 'lodash';
import { isStaticRender } from 'helpers/ContentHelper';
import { helperGetStaticContentUrl } from 'helpers/RouteHelper';

const metaNameTags = {
  robots: 'robots',
  description: 'description',
  viewport: 'viewport',
  appleMediaServiceSubscription: 'apple-media-service-subscription',
  twitterCard: 'twitter:card',
  twitterSite: 'twitter:site',
  twitterAppIdPhone: 'twitter:app:id:iphone',
  twitterAppUrlPhone: 'twitter:app:url:iphone',
  twitterAppIdIpad: 'twitter:app:id:ipad',
  twitterAppUrlIpad: 'twitter:app:url:ipad',
  twitterAppIdGplay: 'twitter:app:id:googleplay',
  twitterAppUrlGplay: 'twitter:app:url:googleplay',
  twitterUrl: 'twitter:url',
  twitterTitle: 'twitter:title',
  twitterDescription: 'twitter:description',
  twitterImage: 'twitter:image',
  appleItunesApp: 'apple-itunes-app-pseudo',
  googlePlayApp: 'google-play-app',
  kindleFireApp: 'kindle-fire-app',
  msApplicationId: 'msApplication-ID',
  msApplicationPackageFamilyName: 'msApplication-PackageFamilyName',
};

const linkRelTags = {
  canonicalUrl: 'canonical',
  shortUrl: 'shortlink',
  appleTouchIcon: 'apple-touch-icon',
  androidTouchIcon: 'android-touch-icon',
  windowsTouchIcon: 'windows-touch-icon',
  favIcon: 'shortcut icon',
};

const metaPropertyTags = {
  fbAppId: 'fb:app_id',
  ogSiteName: 'og:site_name',
  ogType: 'og:type',
  ogUrl: 'og:url',
  ogTitle: 'og:title',
  ogDescription: 'og:description',
  ogImage: 'og:image',
};

/**
 * Set Helmet Tags on the page Head
 * @param {String} title - the page title
 * @param {Object} meta tags - the key value pair for <meta name={key} content={value} />
 * @param {Object} og tags - the key value pair for <meta property={key} content={value} />
 * @param {Array} raw tags - array of react <meta /> component tags.
 */
export const setHelmetTags = data => {
  const tagItems = _.map(data, (value, key) => {
    if (key === 'title')
      return (
        <title key="meta-title" className="ft-seo-tag-title" data-test="web-seo-tag-title">
          {value}
        </title>
      );

    const metaNameTag = metaNameTags[key];
    if (metaNameTag)
      return <meta key={`meta-name-${metaNameTag}`} name={metaNameTag} content={value} />;

    const linkRelTag = linkRelTags[key];
    if (linkRelTag) return <link key={`link-rel-${linkRelTag}`} rel={linkRelTag} href={value} />;

    const metaPropertyTag = metaPropertyTags[key];
    if (metaPropertyTag)
      return (
        <meta key={`meta-property-${metaPropertyTag}`} property={metaPropertyTag} content={value} />
      );

    return null;
  });
  const extraTags = _.get(data, 'tags', null);
  return (
    <Helmet className="ft-seo-tag">
      <html lang="en" translate="no" />
      {tagItems}
      {extraTags}
    </Helmet>
  );
};

export const getFontHelmetTag = () => {
  const staticFontWeights = {
    graphik: [400, 500],
  };
  const staticWeights = _.get(staticFontWeights, FontName);
  const defaultWeight = _.first(staticWeights);
  let fontEmbeds = null;
  if (FontTypekitId) {
    fontEmbeds = (
      <link
        key={`font-${staticWeights}-${FontTypekitId}`}
        rel="stylesheet"
        href={`https://use.typekit.net/${FontTypekitId}.css`}
      />
    );
  } else if (staticWeights && FontName !== 'graphik') {
    fontEmbeds = _.map(staticWeights, weight => {
      if (isStaticRender() && weight !== defaultWeight) return null;
      return (
        <link
          key={`font-${staticWeights}-${weight}`}
          rel="stylesheet"
          href={helperGetStaticContentUrl('fonts', `${FontName}-${weight}.css`)}
        />
      );
    });
  }
  if (!fontEmbeds) return null;
  return <Helmet>{fontEmbeds}</Helmet>;
};

export const brandOverrideMetaTags = contentNetwork => {
  if (!contentNetwork) return null;
  const allowedNetworks = _.get(window.ExternalConfig, 'networkContentWhitelist');
  const isNetworkAllowed = _.includes(allowedNetworks, contentNetwork);
  if (!isNetworkAllowed) {
    return (
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
    );
  }
  return null;
};
